import React from "react";
import { ShieldCheck, Lock, Sparkles, Link2, ListChecks, ArrowRight, CheckCircle2 } from "lucide-react";

function Ring({ pct }) {
  const r = 34, c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, pct)) / 100) * c;
  const color = pct >= 75 ? "var(--ek-secure)" : pct >= 40 ? "#E4B34A" : "var(--ek-primary)";
  return (
    <svg width="88" height="88" viewBox="0 0 88 88" data-testid="integrity-ring">
      <circle cx="44" cy="44" r={r} fill="none" stroke="var(--ek-border)" strokeWidth="7" />
      <circle cx="44" cy="44" r={r} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 44 44)" style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      <text x="44" y="49" textAnchor="middle" fontSize="20" fontWeight="700" fill="var(--ek-text)">{pct}%</text>
    </svg>
  );
}

function Bar({ label, pct, Icon }) {
  return (
    <div className="flex items-center gap-2" data-testid={`integrity-bar-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--ek-primary)" }} />
      <span className="text-xs flex-1 min-w-0 leading-tight" style={{ color: "var(--ek-text-secondary)" }}>{label}</span>
      <div className="h-1.5 w-20 rounded-full overflow-hidden" style={{ background: "var(--ek-border)" }}>
        <div className="h-full" style={{ width: `${pct}%`, background: "var(--ek-primary)", transition: "width 0.5s ease" }} />
      </div>
      <span className="ek-mono-label w-8 text-right">{pct}%</span>
    </div>
  );
}

export default function IntegrityCard({ integrity, matterTitle, onNav }) {
  if (!integrity) return null;
  const c = integrity.counts || {};
  const p = integrity.components || {};
  const n = integrity.nudges || {};
  const nudges = [
    { key: "needs_analysis", count: n.needs_analysis, Icon: Sparkles,
      label: (x) => `${x} file${x === 1 ? "" : "s"} need${x === 1 ? "s" : ""} AI analysis`, to: "vault" },
    { key: "unlinked_evidence", count: n.unlinked_evidence, Icon: Link2,
      label: (x) => `${x} file${x === 1 ? "" : "s"} not linked to a record`, to: "vault" },
    { key: "incidents_without_evidence", count: n.incidents_without_evidence, Icon: ListChecks,
      label: (x) => `${x} record${x === 1 ? "" : "s"} have no linked supporting evidence`, to: "timeline" },
  ].filter((x) => (x.count || 0) > 0);
  return (
    <div className="border rounded-lg p-5 sm:p-6" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="integrity-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" style={{ color: "var(--ek-secure)" }} />
          <span className="font-head font-semibold tracking-tight">Evidence Integrity</span>
        </div>
        {matterTitle && <span className="ek-mono-label truncate max-w-[40%]">{matterTitle}</span>}
      </div>
      <div className="grid grid-cols-3 gap-2 mb-5" data-testid="integrity-headline">
        {[
          { t: "Evidence Integrity", v: integrity.evidence_integrity ?? integrity.overall ?? 0, s: `${c.hashed || 0} of ${c.evidence || 0} files preserved & hashed`, col: "var(--ek-secure)", id: "stat-integrity" },
          { t: "Analysis", v: integrity.analysis_status ?? p.ai_analyzed ?? 0, s: `${c.analyzed || 0} of ${c.evidence || 0} analyzed`, col: "var(--ek-primary)", id: "stat-analysis" },
          { t: "Record Support", v: integrity.record_support ?? p.incidents_supported ?? 0, s: `${c.incidents_with_evidence || 0} of ${c.incidents || 0} records linked`, col: "var(--ek-text-secondary)", id: "stat-record-support" },
        ].map((x) => (
          <div key={x.id} data-testid={x.id} className="rounded-md border p-3 text-center" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }}>
            <div className="text-2xl font-head font-bold" style={{ color: x.col }}>{x.v}%</div>
            <div className="ek-mono-label mt-1">{x.t}</div>
            <div className="text-xs mt-1 leading-tight" style={{ color: "var(--ek-text-muted)" }}>{x.s}</div>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <Bar label="Originals preserved & hashed" pct={p.originals_preserved || 0} Icon={Lock} />
        <Bar label="AI-analyzed (Information Found)" pct={p.ai_analyzed || 0} Icon={Sparkles} />
        <Bar label="Evidence linked to a record" pct={p.evidence_linked || 0} Icon={Link2} />
        <Bar label="Records backed by evidence" pct={p.incidents_supported || 0} Icon={ListChecks} />
      </div>

      <div className="mt-4 pt-3 border-t flex flex-wrap gap-4 text-xs" style={{ borderColor: "var(--ek-border)", color: "var(--ek-text-muted)" }}>
        <span>{c.evidence || 0} evidence</span>
        <span>{c.hashed || 0} hashed</span>
        <span>{c.analyzed || 0} analyzed</span>
        <span>{c.incidents || 0} records</span>
      </div>
    </div>
  );
}
